<template>
  <label :style="labelStyle" :class="labelKls" @click="onClickRoot">
    <input
      v-model="model"
      :class="ns.be('card', 'original')"
      type="checkbox"
      :name="name"
      :tabindex="tabindex"
      :disabled="isDisabled"
      :value="label"
      @change="handleChange"
      @focus="isFocused = true"
      @blur="isFocused = false"
      @click.stop
    />
    <div :class="ns.be('card', 'content')">
      <span :class="cardInnerStyle" />
      <jd-icon v-if="icon || $slots.icon" :size="iconSize" :color="iconColor">
        <component :is="icon" v-if="icon" />
        <slot v-else name="icon" />
      </jd-icon>
      <div :class="ns.be('card', 'content_text')">
        <span
          v-if="$slots.default || label"
          :class="[
            ns.be('card', 'title'),
            ns.is('card__title-checked', isChecked),
          ]"
          :style="isChecked ? activeStyle : undefined"
        >
          <slot>{{ label }}</slot>
        </span>
        <span v-if="slots.desc" :class="ns.be('card', 'desc')">
          <slot name="desc" />
        </span>
        <span v-else-if="desc" :class="ns.be('card', 'desc')">
          {{ desc }}
        </span>
      </div>
    </div>
  </label>
</template>

<script lang="ts" setup>
import { computed, inject, useSlots } from 'vue'
import { iconPropType } from '@jdesign-vue/utils'
import { useNamespace } from '@jdesign-vue/hooks'
import { checkboxGroupContextKey } from './constants'
import { useCheckbox } from './composables'
import { checkboxEmits, checkboxProps } from './checkbox'

import type { CSSProperties } from 'vue'

defineOptions({
  name: 'JdCheckboxCard',
})

const props = defineProps({
  ...checkboxProps,
  desc: {
    type: String,
  },
  icon: {
    type: iconPropType,
  },
  iconColor: {
    type: String,
  },
  iconSize: {
    type: Number,
    default: 44,
  },
  checkedPostion: {
    type: String,
    default: 'right',
  },
})
defineEmits(checkboxEmits)
const slots = useSlots()

const { isFocused, isChecked, isDisabled, model, handleChange, onClickRoot } =
  useCheckbox(props, slots)
const checkboxGroup = inject(checkboxGroupContextKey, undefined)
const ns = useNamespace('checkbox')

const activeStyle = computed<CSSProperties>(() => {
  const fillValue = checkboxGroup?.fill?.value ?? ''
  return {
    backgroundColor: fillValue,
    borderColor: fillValue,
    color: checkboxGroup?.textColor?.value ?? '',
    boxShadow: fillValue ? `-1px 0 0 0 ${fillValue}` : undefined,
  }
})

const labelKls = computed(() => {
  return [
    ns.b('card'),
    ns.is('disabled', isDisabled.value),
    ns.is('checked', isChecked.value),
    ns.is('focus', isFocused.value),
  ]
})
const labelStyle = computed(() => {
  const icon = slots.icon?.()
  const width = props.icon || icon ? Number(props.iconSize) + 144 : 144
  return {
    'max-width': `${width + (props.checkedPostion === 'left' ? 24 : 0)}px`,
  }
})
const cardInnerStyle = computed(() => {
  return [
    ns.be('card', 'inner'),
    props.checkedPostion === 'left'
      ? ns.be('card', 'inner-left')
      : ns.be('card', 'inner-right'),
  ]
})
</script>
